// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。

// I15：工作空间/导航 API client（02 §8 冻结契约的 typed 面）。
// owner 一律由网关认证注入——本 client 不传 user_id 参数；
// 错误语义透传（403/404/409/422），409 携带服务端 current_revision。

import { ApiBase } from './apiBase';

export interface Workspace {
  id: string;
  kind: 'course' | 'research';
  owner: string;
  title: string;
  revision: number;
  archived: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface SessionNav {
  // 审计 P1-1：网关 SessionResponse 序列化键为 session_id（非 id）
  session_id: string;
  user_id: string | null;
  title: string | null;
  workspace_id: string | null;
  is_pinned: boolean;
  is_archived: boolean;
  session_kind: string | null;
  nav_revision: number;
  updated_at: string | null;
  created_at: string | null;
}

export interface SessionNavSearchResult {
  sessions: SessionNav[];
  next_cursor: string | null;
}

export class ApiWorkspaces extends ApiBase {
  async listWorkspaces(includeArchived = false) {
    const response = await this.client.get('/v1/workspaces', {
      params: { include_archived: String(includeArchived) },
    });
    return response.data as { workspaces: Workspace[] };
  }

  async createWorkspace(kind: 'course' | 'research', title: string) {
    const response = await this.client.post('/v1/workspaces', { kind, title });
    return response.data as Workspace;
  }

  async updateWorkspace(
    workspaceId: string,
    patch: { title?: string; archived?: boolean; expected_revision?: number },
  ) {
    const response = await this.client.patch(`/v1/workspaces/${workspaceId}`, patch);
    return response.data as Workspace;
  }

  async deleteWorkspace(workspaceId: string, detach = false) {
    const response = await this.client.delete(`/v1/workspaces/${workspaceId}`, {
      params: detach ? { detach: 'true' } : {},
    });
    return response.data as { detached_sessions: number };
  }

  // 会话导航：服务端标题搜索（keyset 游标），pin/archive/归属 CAS
  async searchSessionNavigation(params: {
    query?: string;
    workspace_id?: string;
    archived?: boolean;
    limit?: number;
    cursor?: string;
  }) {
    const response = await this.client.get('/v1/sessions/', {
      params: {
        ...(params.query ? { query: params.query } : {}),
        ...(params.workspace_id != null ? { workspace_id: params.workspace_id } : {}),
        ...(params.archived != null ? { archived: String(params.archived) } : {}),
        ...(params.limit ? { page_size: params.limit } : {}),
        ...(params.cursor ? { cursor: params.cursor } : {}),
      },
    });
    return response.data as SessionNavSearchResult;
  }

  async updateSessionNavigation(
    sessionId: string,
    patch: {
      expected_revision?: number;
      workspace_id?: string;
      is_pinned?: boolean;
      is_archived?: boolean;
    },
  ) {
    const response = await this.client.patch(`/v1/sessions/${sessionId}/navigation`, patch);
    return response.data as {
      session_id: string;
      workspace_id: string | null;
      is_pinned: boolean;
      is_archived: boolean;
      nav_revision: number;
    };
  }
}
